import { UnknownAction } from "redux-saga";
import {
  DeleteLinkRequest,
  FetchingLinkResponse,
  GenerateLinkRequest,
  GenerateLinkResponse,
  Link,
  UpdateLinkRequest,
} from "../../types/link";
import {
  GENERATE_LINK_REQUEST,
  GENERATE_LINK_SUCCEEDED,
  UPDATE_LINK_REQUEST,
  UPDATING_LINK_SUCCEEDED,
  FETCHING_ALL_LINKS_SUCCEEDED,
  DELETE_LINK_REQUEST,
  FETCH_ALL_LINKS_REQUEST,
  DELETING_LINK_SUCCEEDED,
  GENERATE_LINK_FAILED,
  UPDATING_LINK_FAILED,
  FETCHING_ALL_LINKS_FAILED,
  DELETING_LINK_FAILED,
  INVALIDATE_ALL_LINK_ITEMS,
  INVALIDATE_LINK_UPDATIONS,
  RESET_GENERATE_LINK_STATE,
} from "../actions/actionTypes";
import _ from "lodash";

interface LinkInitialState {
  generateLink: LinkState;
  listOfLinks: ListOfLinksState;
  pagination: PaginationState;
  updation: {
    [key: string]: UpdateLinkState;
  } | null;
  deletion: {
    [key: string]: DeleteLinkState;
  } | null;
}

interface LinkErrorResponse {
  id: string;
  error: any;
}
interface ListOfLinksState {
  loading: boolean;
  items: Link[];
  error?: any;
}
interface DeleteLinkState {
  deleting: boolean;
  deleted: boolean;
  id: string;
  error?: any;
}
interface UpdateLinkState {
  updating: boolean;
  updated: boolean;
  id: string;
  item: Link | null;
  error?: any;
}

interface LinkState {
  creating: boolean;
  created: boolean;
  type: string | null;
  link: Link | null;
  error?: any;
}
interface PaginationState {
  currentPage: number;
  totalPages: number;
  totalRecordsCount: number;
  hasMore: boolean;
}
const initialGenerateLinkState: LinkState = {
  creating: false,
  created: false,
  type: null,
  link: null,
};
const initailListofLinksState: ListOfLinksState = {
  loading: false,
  items: [],
  error: null,
};

const initialPaginationState: PaginationState = {
  currentPage: 0,
  totalPages: 0,
  totalRecordsCount: 0,
  hasMore: false,
};
const initialState: LinkInitialState = {
  generateLink: initialGenerateLinkState,
  listOfLinks: initailListofLinksState,
  pagination: initialPaginationState,
  updation: null,
  deletion: null,
};

type Action = UnknownAction;

export const linkReducer = (state = initialState, action: Action) => {
  switch (action.type) {
    case GENERATE_LINK_REQUEST: {
      const { type } = action.payload as GenerateLinkRequest;

      return {
        ...state,
        generateLink: {
          ...state.generateLink,
          creating: true,
          created: false,
          type: type
        },
      };
    }
    case GENERATE_LINK_SUCCEEDED: {
      const response = action.payload as GenerateLinkResponse;

      return {
        ...state,
        generateLink: {
          ...state.generateLink,
          link: response,
          creating: false,
          created: true,
        },
      };
    }
    case GENERATE_LINK_FAILED: {
      return {
        ...state,
        generateLink: {
          ...state.generateLink,
          creating: false,
          created: false,
          error: action.payload,
        },
      };
    }

    case FETCH_ALL_LINKS_REQUEST: {
      return {
        ...state,
        listOfLinks: {
          ...state.listOfLinks,
          items: [],
          loading: true,
        },
      };
    }
    case FETCHING_ALL_LINKS_SUCCEEDED: {
      const response = action.payload as FetchingLinkResponse;

      const items = _.get(response, "records", []);
      return {
        ...state,
        listOfLinks: {
          ...state.listOfLinks,
          loading: false,
          items: items,
        },
        pagination: {
          currentPage: _.get(response, "currentPage"),
          totalPages: _.get(response, "totalPages"),
          totalRecordsCount: _.get(response, "totalRecordsCount"),
          hasMore:
            _.get(response, "currentPage", 0) <
            _.get(response, "totalPages", 0) - 1,
        },
      };
    }
    case FETCHING_ALL_LINKS_FAILED: {
      return {
        ...state,
        listOfLinks: {
          ...state.listOfLinks,
          error: action.payload,
        },
      };
    }
    case INVALIDATE_ALL_LINK_ITEMS: {
      return {
        ...state,
        listOfLinks: initailListofLinksState,
        pagination: initialPaginationState,
      };
    }

    case UPDATE_LINK_REQUEST: {
      const request = action.payload as UpdateLinkRequest;
      return {
        ...state,
        updation: {
          ...state.updation,
          [request.id]: {
            updating: true,
            updated: false,
            id: request.id,
            item: null,
          },
        },
      };
    }
    case UPDATING_LINK_SUCCEEDED: {
      const response = action.payload as Link;

      const updatedLinks = state.listOfLinks.items.map((link) => {
        if (link.id === response.id) {
          return response;
        }
        return link;
      });

      return {
        ...state,
        updation: {
          ...state.updation,
          [response.id]: {
            updating: false,
            updated: true,
            id: response.id,
            item: response,
          },
        },
        generateLink: {
          ...state.generateLink,
          link:
            response.id === _.get(state.generateLink.link, "id")
              ? response
              : state.generateLink.link,
        },
        listOfLinks: {
          ...state.listOfLinks,
          items: updatedLinks,
        },
      };
    }
    case UPDATING_LINK_FAILED: {
      const payload = action.payload as LinkErrorResponse;
      const id = payload.id;

      return {
        ...state,
        updation: {
          ...state.updation,
          [id]: {
            updating: false,
            updated: false,
            id: id,
            error: payload.error,
          },
        },
      };
    }

    case DELETE_LINK_REQUEST: {
      const request = action.payload as DeleteLinkRequest;
      return {
        ...state,
        deletion: {
          ...state.deletion,
          [request.id]: {
            deleting: true,
            deleted: false,
            id: request.id,
          },
        },
      };
    }
    case DELETING_LINK_SUCCEEDED: {
      const payload = action.payload as DeleteLinkRequest;
      const id = payload.id;

      const filteredLinks = state.listOfLinks.items.filter(
        (link: Link) => link.id !== id,
      );

      return {
        ...state,
        deletion: {
          ...state.deletion,
          [id]: {
            deleting: false,
            deleted: true,
            id: id,
          },
        },
        listOfLinks: {
          ...state.listOfLinks,
          items: filteredLinks,
        },
        generateLink: {
          ...state.generateLink,
          link:
            id === _.get(state.generateLink.link, "id")
              ? null
              : state.generateLink.link,
        }
      };
    }
    case DELETING_LINK_FAILED: {
      const payload = action.payload as LinkErrorResponse;
      const id = payload.id;

      return {
        ...state,
        deletion: {
          ...state.deletion,
          [id]: {
            deleting: false,
            deleted: false,
            id: id,
            error: payload.error,
          },
        },
      };
    }

    case INVALIDATE_LINK_UPDATIONS: {
      const payload = action.payload as { id: string };
      return {
        ...state,
        updation: {
          ...state.updation,
          [payload.id]: {
            updating: false,
            updated: false,
            id: payload.id,
            item: null,
          },
        },
      };
    }
    case RESET_GENERATE_LINK_STATE: {
      return {
        ...state,
        generateLink: initialGenerateLinkState,
      };
    }
    default:
      return state;
  }
};
